import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';

class ComboSelectorComponent extends Component {
  requiredRefs = ['liveRegion'];
  #section; #customGallery; #customGalleryIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#ensureSingleSelection();
    this.#syncSelectedDisplay();
    this.#syncPageProductDisplay();

    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  selectCard(event) {
    const card = event.target.closest('[data-combo-card]');
    if (!card) return;

    this.querySelectorAll('[data-combo-card]').forEach((el) => el.classList.remove('is-selected'));
    card.classList.add('is-selected');

    this.#syncSelectedDisplay();
    this.#syncPageProductDisplay();
  }

  async addToCart() {
    const items = this.#selectedItems;
    if (!items.length) return;

    this.refs.addToCartButton.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify({ items }) }));
      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, data.message));
        this.#announce(data.message);
        return;
      }

      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'combo-selector',
          itemCount,
          sections: data.sections,
        })
      );

      this.#announce(Theme.translations.added);
    } catch (error) {
      console.error(error);
    } finally {
      this.refs.addToCartButton.disabled = false;
    }
  }

  async buyNow() {
    const items = this.#selectedItems;
    if (!items.length) return;

    this.refs.buyNowButton.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify({ items }) }));
      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, data.message));
        this.#announce(data.message);
        this.refs.buyNowButton.disabled = false;
        return;
      }

      // Cart permalink checkout isn't used here since items may span multiple products/variants.
      window.location.href = Theme.routes.checkout_url || '/checkout';
    } catch (error) {
      console.error(error);
      this.refs.buyNowButton.disabled = false;
    }
  }

  get #selectedCard() {
    return this.querySelector('[data-combo-card].is-selected') || this.querySelector('[data-combo-card]');
  }

  get #selectedItems() {
    const card = this.#selectedCard;
    if (!card) return [];

    return (card.dataset.items || '')
      .split(',')
      .filter(Boolean)
      .map((pair) => {
        const [id, quantity] = pair.split(':');
        return { id: Number(id), quantity: Number(quantity) || 1 };
      });
  }

  #ensureSingleSelection() {
    const cards = [...this.querySelectorAll('[data-combo-card]')];
    if (!cards.length) return;

    const selected = cards.filter((card) => card.classList.contains('is-selected'));

    if (selected.length === 0) {
      cards[0].classList.add('is-selected');
    } else if (selected.length > 1) {
      selected.slice(1).forEach((card) => card.classList.remove('is-selected'));
    }
  }

  #syncSelectedDisplay() {
    const card = this.#selectedCard;
    if (!card) return;

    const titleEl = this.querySelector('[ref="selectedTitle"]');
    const priceEl = this.querySelector('[ref="selectedPrice"]');

    if (titleEl) titleEl.textContent = card.dataset.title || '';
    if (priceEl) priceEl.textContent = card.dataset.price || '';
  }

  #handleVariantUpdate = () => {
    queueMicrotask(() => this.#syncPageProductDisplay());
  };

  #syncPageProductDisplay() {
    const card = this.#selectedCard;
    if (!card) return;

    const titleEl = document.querySelector('h1');
    if (titleEl) titleEl.textContent = card.dataset.title || '';

    const priceContainer = document.querySelector('product-price [ref="priceContainer"]');
    if (priceContainer) {
      const price = card.dataset.price || '';
      const compareAt = card.dataset.compareAt || '';
      priceContainer.innerHTML = compareAt
        ? `<span class="price">${price}</span><span class="compare-at-price">${compareAt}</span>`
        : `<span class="price">${price}</span>`;
    }

    this.#syncGallery((card.dataset.images || '').split('|').filter(Boolean));

    const variantId = (card.dataset.items || '').split(',')[0]?.split(':')[0];
    if (variantId) {
      document.querySelectorAll('input[ref="variantId"]').forEach((input) => {
        input.value = variantId;
      });
    }
  }

  #syncGallery(images) {
    const nativeGallery = document.querySelector('media-gallery');
    if (!nativeGallery) return;

    if (!images.length) {
      this.#customGallery?.remove();
      this.#customGallery = null;
      nativeGallery.style.display = '';
      return;
    }

    nativeGallery.style.display = 'none';
    this.#customGalleryIndex = 0;

    if (!this.#customGallery) {
      this.#customGallery = document.createElement('div');
      this.#customGallery.className = 'combo-gallery';
      nativeGallery.insertAdjacentElement('afterend', this.#customGallery);
    }

    this.#renderCustomGallery(images);
  }

  #renderCustomGallery(images) {
    const gallery = this.#customGallery;
    const index = this.#customGalleryIndex;

    gallery.innerHTML = `
      <div class="combo-gallery__main">
        <img class="combo-gallery__image" src="${images[index]}" alt="">
        ${
          images.length > 1
            ? `<button type="button" class="combo-gallery__arrow combo-gallery__arrow--prev" aria-label="Previous image">‹</button>
               <button type="button" class="combo-gallery__arrow combo-gallery__arrow--next" aria-label="Next image">›</button>`
            : ''
        }
      </div>
      ${
        images.length > 1
          ? `<div class="combo-gallery__dots">${images
              .map((_, i) => `<button type="button" class="combo-gallery__dot${i === index ? ' is-active' : ''}" aria-label="Image ${i + 1}"></button>`)
              .join('')}</div>`
          : ''
      }
    `;

    gallery.querySelector('.combo-gallery__arrow--prev')?.addEventListener('click', () => this.#stepCustomGallery(images, -1));
    gallery.querySelector('.combo-gallery__arrow--next')?.addEventListener('click', () => this.#stepCustomGallery(images, 1));
    gallery.querySelectorAll('.combo-gallery__dot').forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.#customGalleryIndex = i;
        this.#renderCustomGallery(images);
      });
    });
  }

  #stepCustomGallery(images, delta) {
    this.#customGalleryIndex = (this.#customGalleryIndex + delta + images.length) % images.length;
    this.#renderCustomGallery(images);
  }

  #announce(text) {
    this.refs.liveRegion.textContent = text;
  }
}

if (!customElements.get('combo-selector-component')) {
  customElements.define('combo-selector-component', ComboSelectorComponent);
}
